import React from 'react';

import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';

import { useIntl } from 'src/hooks/intl';
import { ResultStatus } from 'src/types/custom-merge-checks';

import { InProgressStatusIcon } from './icons/in-progress-status-icon';
import messages from './status-icon.i18n';

export const StatusIcon = ({
  status,
  required,
  testIdPrefix,
}: {
  status: ResultStatus | null;
  required?: boolean;
  testIdPrefix?: string;
}) => {
  const { formatMessage } = useIntl();

  switch (status) {
    case 'SUCCESS':
      return (
        <CheckCircleIcon
          testId={`${testIdPrefix}--success`}
          label={formatMessage(messages.successIconLabel)}
          primaryColor={token('color.icon.success')} // Green600
        />
      );
    // don't distinguish in UI between "IN-PROGRESS" and "PENDING" as it's an unnecessary distinction for the user
    case 'PENDING':
    case 'IN-PROGRESS':
      return (
        <InProgressStatusIcon
          testId={`${testIdPrefix}--inprogress`}
          label={formatMessage(messages.inProgressIconLabel)}
          color={token('color.icon.information')} // Blue600
        />
      );
    case 'ERROR':
    case 'FAILED': {
      // Explicit check for required === false to determine if the check is a recommended check.
      // This is so that null or undefined values are not treated as recommended checks.
      // E.g. the overall status icon will not provide a `required` value.
      // Recommended checks are grey because they do not block a PR merge
      const tokenFn =
        required === false
          ? token('color.icon') // N800
          : token('color.icon.warning'); // Orange600
      return (
        <WarningIcon
          testId={`${testIdPrefix}--warning`}
          label={formatMessage(messages.failedIconLabel)}
          primaryColor={tokenFn}
        />
      );
    }
    case 'IMPENDING':
      return (
        <RecentIcon
          testId={`${testIdPrefix}--recent`}
          label={formatMessage(messages.impendingIconLabel)}
          primaryColor={token('color.icon')} // Neutral800
        />
      );
    default:
      return null;
  }
};
